import { Component, Input } from '@angular/core';
import {
  CtBinaryOperator,
  CtDatatableActionConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelOperator
} from "@ctsolution/ct-framework";
import { WorkOrderRouteData } from "../../../_core/route-data/work-order.route-data";
import { Router } from "@angular/router";
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../_core/constants';
import { FileGet } from '../../../_core/classes/file-get';
import { FileService } from '../../../_core/lib/file.service';
import { CTMWorkOrderViewType } from '../../../_core/enum/work-order-type';
import { WorkOrderService } from '../../work-order/work-order.service';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';
import { CTConfigurationModel } from '../../../_core/classes/ct-configuration-model';

@Component({
  selector: 'app-company-contract',
  template: `
    <div *ngIf="configuration">

    <h5> {{ (companyWorkOrderConfiguration?.label ?? 'workorder_quotation') | translate }} </h5>

      <ct-model *ngIf="configuration" [configuration]="configuration"></ct-model>

    </div>
  `
})
export class CompanyContractComponent {

  @Input() masterConfiguration: CtModelConfiguration<any> | null = null;
  configuration: CtModelConfiguration<any> | null = null;
  companyWorkOrderConfiguration : CTConfigurationModel | null = null;


  fileHandlingCTA: CtDatatableActionConfiguration = new CtDatatableActionConfiguration()
    .setColor("primary")
    .setIcon("folder_zip")
    .setDynamicClass(value => !value['Files'].length ? `no-data` : null)

  datatableConfiguration = DEFAULT_DATATABLE_CONFIGURATION();


  constructor(private router: Router, private fileService: FileService, private workOrderService: WorkOrderService, private managerService : CtManagerService) {
  }

  ngOnInit() {

    if (this.masterConfiguration?.RouteData?.id) {

      this.setup();

    }

  }

  async setup() {

    // Titolo della sezione dichiarabile per-client nel MenuJson (`label`): serve ai ruoli per cui
    // questa sezione non e' "Contratti". Il default va impostato esplicitamente: `label` e' opzionale
    // senza inizializzatore, quindi senza `setLabel` non e' una chiave dell'oggetto e il merge di
    // `getComponentOverride`, che itera `Object.keys`, non lo applicherebbe.
    this.companyWorkOrderConfiguration = CTConfigurationModel.create()
      .setVisible(false)
      .setLabel('workorder_quotation')

    await this.managerService.getComponentOverride("company", "companyWorkOrderConfiguration", this.companyWorkOrderConfiguration).then((component) => {
      return;
    });

    // L'icona dello ZIP dei documenti si aggiunge solo se il MenuJson non la spegne (`downloadZipEnabled`):
    // va deciso qui, dopo la lettura dell'override, perche' la datatable non permette di toglierla dopo.
    if (this.companyWorkOrderConfiguration.downloadZipEnabled) {

      this.datatableConfiguration
        .setCustomDatatableAction(this.fileHandlingCTA,
          (async (event: any) => {

            if (event.Files.length > 0)
              this.downloadZip(event.Files[0].Endpoint, event.Name)

            //siamo d'accordo con le api che il primo elemento sia sempre lo zip, altrimenti si avrà un type per definirlo
          }));

    }

    const configuration = CtModelConfiguration
      .create()
      .setRouteData(WorkOrderRouteData())
      .setCTDatatableConfiguration(this.datatableConfiguration)
      .setDisableAutoQueryFilter(true)
      .setOnEdit(event => {

        // Indirizzo esterno dichiarato per-client nel MenuJson: per i ruoli che gestiscono il
        // contratto su un sito esterno, la modifica porta li' e non al dettaglio del contratto.
        // Nuova scheda, come gli altri collegamenti della dashboard, per non far perdere
        // l'applicazione; 'noopener' perche' il sito e' di terzi e non deve poter agire su questa scheda.
        const editExternalUrl = this.companyWorkOrderConfiguration?.editExternalUrl;

        if (editExternalUrl) {

          window.open(editExternalUrl, '_blank', 'noopener');
          return;

        }

        let workOrderType = CTMWorkOrderViewType.Standard;

        if (event.Quotation) workOrderType = CTMWorkOrderViewType.Quotation;

        const controllerRoute = this.workOrderService.getRoutePathController(workOrderType);

        const path = ['/', ...controllerRoute, 'edit', event.Oid];

        this.router.navigate(path);

      })
      .setFieldsCustomTemplates([
        { prop: 'Company', visible: false },
        { prop: 'CompanyFiscalCode', visible: false },
        { prop: 'CompanyVAT', visible: false },
        { prop: 'Quotation', visible: this.companyWorkOrderConfiguration?.visible ?? false }
      ])



    configuration
      .initialized()
      .subscribe(() => {

        this.configuration?.setOperations([CtModelOperator.Update]);
        this.applyMenuJsonVisibleColumns();

      })

    configuration
      .filterValues
      .addConstantFilter(CTModelDatatableFilter
        .create()
        .setField('Company.Oid')
        .setValue(this.masterConfiguration?.RouteData?.id)
        .setOperatorType(CtBinaryOperator.Equal))

    // Filtri costanti dichiarati per-client nel MenuJson: servono quando un ruolo deve vedere solo
    // una parte dei contratti del cliente, non tutti. Senza dichiarazione l'elenco e' vuoto e la
    // sezione resta quella storica. upsert e non add: se il campo e' gia' filtrato, vince il JSON.
    const constantFilters = this.companyWorkOrderConfiguration?.constantFilters ?? [];

    constantFilters
      .forEach(filter => configuration
        .filterValues
        .upsertConstantFilter(
          CTModelDatatableFilter
            .create()
            .setField(filter.field)
            .setValue(filter.value)
            .setOperatorType(filter.operatorType ?? CtBinaryOperator.Equal)));

    this.configuration = configuration;

  }

  /**
   * Nasconde le colonne non dichiarate in `visibleColumns` nel MenuJson. Senza dichiarazione non fa
   * nulla: gli altri client restano con le colonne di oggi.
   *
   * Si fa in `initialized()` perche' i nomi delle colonne arrivano dal backend: a quel punto
   * `CTFieldInfos` e' popolato, ma la datatable non e' ancora costruita e legge i
   * `fieldsCustomTemplates` solo dopo, quindi `visible: false` fa ancora effetto. I template gia'
   * dichiarati (Company, Quotation, ...) restano: la lista viene unita, non sostituita.
   */
  private applyMenuJsonVisibleColumns(): void {

    const visibleColumns = this.companyWorkOrderConfiguration?.visibleColumns;

    if (!visibleColumns || !this.configuration) return;

    const customTemplates = [...(this.configuration.fieldsCustomTemplates ?? [])];

    (this.configuration.CTFieldInfos ?? [])
      .filter(info => info.IsColumn && !!info.Field && !visibleColumns.includes(info.Field))
      .forEach(info => {

        const existing = customTemplates.find(template => template.prop === info.Field);

        if (existing) {

          existing.visible = false;
          return;

        }

        customTemplates.push({ prop: info.Field!, visible: false });

      });

    this.configuration.setFieldsCustomTemplates(customTemplates);

  }

  //TODO: centralizzare?
  downloadZip(endpoint: string, workorderName: string) {

    const filename = `Documenti_${workorderName}`
    const value: FileGet = {
      Endpoint: endpoint
    };

    this.fileService.downloadAttachmentByEndPoint(value, filename)

    //TODO: potrà essere personalizzata l'etichetta in base alle esigenze
  }

}
